import {
  STATUS_ANALISE_CRITERIO,
  type AnalisePorCriterio,
  type SolicitacaoAnalise,
  type SolicitacaoDetalheStatus,
} from "../../../../services";
import {
  analiseCriterioComRespostaAposSolicitacao,
  listarAnalisesCriterio,
  listarCiclosSolicitacaoAdequacaoOrdenados,
  pickObservacaoAnalistaMaisRecente,
  pickAnaliseAtualPorCriterio,
  pickAnaliseLinhaEdicaoParecer,
  pickObservacaoAnalistaUltimoCicloEncerrado,
  pickObservacaoSindicatoUltimoCicloEncerrado,
} from "../../../../domain/shipping/analiseCriterioList";

// Converte o GET /analise em estruturas de UI por seção/critério (status, observações e histórico).
export type AnaliseStatusUI = "CONFORME" | "NAO_CONFORME" | "DEVOLVER" | "";

export type AnaliseCamposPorSecao = {
  status: AnaliseStatusUI;
  observacaoAnalista: string;
  observacaoInterna: string;
  
  registroAnaliseId?: number | null;
  
  criterioIdApi?: number;
};

const normalizarStatusPorNome = (
  statusNome: string | null | undefined,
): AnaliseStatusUI => {
  const s = String(statusNome || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toUpperCase();

  if (s.includes("NAO CONFORME")) return "NAO_CONFORME";
  if (s.includes("CONFORME")) return "CONFORME";
  if (s.includes("DEVOLVER")) return "DEVOLVER";
  return "";
};


const normalizarStatusAnalise = (
  status: SolicitacaoDetalheStatus | null | undefined,
): AnaliseStatusUI => {
  const id = status?.id;
  if (id != null && Number.isFinite(id)) {
    switch (id) {
      case STATUS_ANALISE_CRITERIO.Pendente:
        return "";
      case STATUS_ANALISE_CRITERIO.Conforme:
        return "CONFORME";
      case STATUS_ANALISE_CRITERIO.NaoConforme:
        return "NAO_CONFORME";
      case STATUS_ANALISE_CRITERIO.Devolver:
        return "DEVOLVER";
      default:
        break;
    }
  }
  return normalizarStatusPorNome(status?.nome);
};

function ultimoStatusNaoPendenteDoHistorico(c: {
  analise?: AnalisePorCriterio | AnalisePorCriterio[] | null;
}): AnaliseStatusUI {
  const arr = listarAnalisesCriterio(c.analise);
  for (let i = arr.length - 1; i >= 0; i -= 1) {
    const s = normalizarStatusAnalise(arr[i]?.status);
    if (s === "CONFORME" || s === "NAO_CONFORME" || s === "DEVOLVER") return s;
  }
  return "";
}


export type HistoricoAdequacaoPorAnaliseKey = Record<
  string,
  Pick<
    AnalisePorCriterio,
    | "id"
    | "solicitadoEm"
    | "adequadoEm"
    | "observacaoAnalista"
    | "observacaoSindicato"
  >
>;


export type AdequacaoCicloHistoricoItem = Pick<
  AnalisePorCriterio,
  "id" | "solicitadoEm" | "adequadoEm" | "observacaoAnalista" | "observacaoSindicato"
>;


export type CiclosHistoricoAdequacaoPorAnaliseKey = Record<
  string,
  AdequacaoCicloHistoricoItem[]
>;

function chavesCriterioAnalise(c: {
  id: number;
  criterioId?: number;
}): string[] {
  const keys = new Set<string>();
  if (typeof c.id === "number" && Number.isFinite(c.id) && c.id > 0) {
    keys.add(String(c.id));
  }
  if (
    typeof c.criterioId === "number" &&
    Number.isFinite(c.criterioId) &&
    c.criterioId > 0 &&
    c.criterioId !== c.id
  ) {
    keys.add(String(c.criterioId));
  }
  return [...keys];
}

export function buildCiclosHistoricoAdequacaoPorAnaliseKey(
  analise: SolicitacaoAnalise | null,
): CiclosHistoricoAdequacaoPorAnaliseKey {
  const out: CiclosHistoricoAdequacaoPorAnaliseKey = {};
  if (!analise?.grupos?.length) return out;
  for (const g of analise.grupos) {
    for (const c of g.criterios || []) {
      const ciclos = listarCiclosSolicitacaoAdequacaoOrdenados(c.analise);
      if (ciclos.length === 0) continue;
      const payload = ciclos.map((x) => ({
        id: x.id,
        solicitadoEm: x.solicitadoEm ?? null,
        adequadoEm: x.adequadoEm ?? null,
        observacaoAnalista: x.observacaoAnalista ?? null,
        observacaoSindicato: x.observacaoSindicato ?? null,
      }));
      for (const key of chavesCriterioAnalise(c)) {
        out[key] = payload;
      }
    }
  }
  return out;
}


export function buildHistoricoAdequacaoPorAnaliseKey(
  analise: SolicitacaoAnalise | null,
): HistoricoAdequacaoPorAnaliseKey {
  if (!analise?.grupos?.length) return {};
  const out: HistoricoAdequacaoPorAnaliseKey = {};
  for (const g of analise.grupos) {
    for (const c of g.criterios || []) {
      const primary = pickAnaliseAtualPorCriterio(c.analise);
      if (!primary?.solicitadoEm) continue;
      const cicloAtualPendente = !analiseCriterioComRespostaAposSolicitacao(primary);
      const ciclosOrdenados = listarCiclosSolicitacaoAdequacaoOrdenados(c.analise);
      const observacaoAnalistaUltimaSolicitacao = (() => {
        for (let i = ciclosOrdenados.length - 1; i >= 0; i--) {
          const o = ciclosOrdenados[i]?.observacaoAnalista;
          if (o != null && String(o).trim() !== "") return String(o);
        }
        return null;
      })();
      const obsAnalista =
        primary.observacaoAnalista != null &&
        String(primary.observacaoAnalista).trim() !== ""
          ? primary.observacaoAnalista
          : cicloAtualPendente
            ? observacaoAnalistaUltimaSolicitacao ??
              pickObservacaoAnalistaMaisRecente(c.analise, primary) ??
              pickObservacaoAnalistaUltimoCicloEncerrado(c.analise)
            : pickObservacaoAnalistaUltimoCicloEncerrado(c.analise);
      const obsSindicato =
        primary.observacaoSindicato != null &&
        String(primary.observacaoSindicato).trim() !== ""
          ? primary.observacaoSindicato
          : cicloAtualPendente
            ? null
            : pickObservacaoSindicatoUltimoCicloEncerrado(c.analise);
      const payload = {
        id: primary.id,
        solicitadoEm: primary.solicitadoEm ?? null,
        adequadoEm: primary.adequadoEm ?? null,
        observacaoAnalista: obsAnalista ?? null,
        observacaoSindicato: obsSindicato ?? null,
      };
      for (const key of chavesCriterioAnalise(c)) {
        out[key] = payload;
      }
    }
  }
  return out;
}

function criterioNegocioIdParaMerge(
  key: string,
  campos?: AnaliseCamposPorSecao | null,
): number | null {
  if (
    typeof campos?.criterioIdApi === "number" &&
    Number.isFinite(campos.criterioIdApi) &&
    campos.criterioIdApi > 0
  ) {
    return campos.criterioIdApi;
  }
  const n = Number(key);
  return Number.isFinite(n) && n > 0 ? n : null;
}


export function mergeAnaliseCamposComApi(
  analiseBase: SolicitacaoAnalise | null | undefined,
  camposUsuario: Record<string, AnaliseCamposPorSecao>,
): Record<string, AnaliseCamposPorSecao> {
  if (!analiseBase) return { ...camposUsuario };

  const fromApi = buildAnaliseCamposPorSecaoId(analiseBase);
  const out: Record<string, AnaliseCamposPorSecao> = { ...fromApi };

  const apiEntryByCriterioId = new Map<string, AnaliseCamposPorSecao>();
  for (const [k, v] of Object.entries(fromApi)) {
    const cid = criterioNegocioIdParaMerge(k, v);
    if (cid == null) continue;
    apiEntryByCriterioId.set(String(cid), v);
  }

  for (const [k, u] of Object.entries(camposUsuario)) {
    const baseKey = fromApi[k];
    const cidUsuario = criterioNegocioIdParaMerge(k, u);
    const baseByCid =
      cidUsuario != null ? apiEntryByCriterioId.get(String(cidUsuario)) : null;
    const base = baseKey ?? baseByCid ?? null;

    out[k] = {
      ...(base ?? {
        status: "",
        observacaoAnalista: "",
        observacaoInterna: "",
      }),
      ...u,
      registroAnaliseId:
        base?.registroAnaliseId != null && base.registroAnaliseId > 0
          ? base.registroAnaliseId
          : u.registroAnaliseId != null && u.registroAnaliseId > 0
            ? u.registroAnaliseId
            : null,
      criterioIdApi:
        cidUsuario ??
        criterioNegocioIdParaMerge(k, base) ??
        criterioNegocioIdParaMerge(k, null) ??
        undefined,
    };
  }

  return out;
}


function mesclarDuasEntradasAliasAnalise(
  a: AnaliseCamposPorSecao,
  b: AnaliseCamposPorSecao,
): AnaliseCamposPorSecao {
  const pri = (s: AnaliseStatusUI | undefined) => {
    if (s === "CONFORME") return 4;
    if (s === "NAO_CONFORME") return 3;
    if (s === "DEVOLVER") return 2;
    return 0;
  };
  const pa = pri(a.status);
  const pb = pri(b.status);
  if (pb > pa) return { ...b };
  if (pa > pb) return { ...a };
  const ra = a.registroAnaliseId ?? 0;
  const rb = b.registroAnaliseId ?? 0;
  if (rb > ra) return { ...b };
  return { ...a };
}


function reduzirMapPorCriterioLogico(
  map: Record<string, AnaliseCamposPorSecao>,
): AnaliseCamposPorSecao[] {
  const porLogico = new Map<string, AnaliseCamposPorSecao>();
  for (const [k, c] of Object.entries(map)) {
    const lk =
      typeof c?.criterioIdApi === "number" &&
      Number.isFinite(c.criterioIdApi) &&
      c.criterioIdApi > 0
        ? `cid:${c.criterioIdApi}`
        : `k:${k}`;
    const prev = porLogico.get(lk);
    porLogico.set(
      lk,
      prev ? mesclarDuasEntradasAliasAnalise(prev, c) : { ...c },
    );
  }
  return [...porLogico.values()];
}


export function temAlgumCriterioDevolver(
  map: Record<string, AnaliseCamposPorSecao>,
): boolean {
  return reduzirMapPorCriterioLogico(map).some((c) => c?.status === "DEVOLVER");
}


export function coletarChavesCriteriosAnalise(
  analiseBase: SolicitacaoAnalise | null | undefined,
  chavesDasSecoes: string[],
): string[] {
  const s = new Set<string>(chavesDasSecoes);
  if (analiseBase?.grupos?.length) {
    for (const g of analiseBase.grupos) {
      for (const c of g.criterios || []) {
        for (const key of chavesCriterioAnalise(c)) {
          s.add(key);
        }
      }
    }
  }
  return [...s];
}


export function titulosCriteriosAnalise(
  analiseBase: SolicitacaoAnalise | null | undefined,
  titulosDasSecoes: Record<string, string>,
): Record<string, string> {
  const m: Record<string, string> = { ...titulosDasSecoes };
  if (analiseBase?.grupos?.length) {
    for (const g of analiseBase.grupos) {
      for (const c of g.criterios || []) {
        const t = String(c.titulo || "").trim();
        for (const k of chavesCriterioAnalise(c)) {
          if (!m[k]?.trim()) {
            m[k] = t || `Critério ${k}`;
          }
        }
      }
    }
  }
  return m;
}


export function buildAnaliseCamposPorSecaoId(
  analise: SolicitacaoAnalise | null,
): Record<string, AnaliseCamposPorSecao> {
  if (!analise?.grupos?.length) return {};

  const out: Record<string, AnaliseCamposPorSecao> = {};
  for (const g of analise.grupos) {
    for (const c of g.criterios || []) {
      const a = pickAnaliseLinhaEdicaoParecer(c.analise);
      const statusComFallback: SolicitacaoDetalheStatus | null | undefined =
        a?.status ??
        c.status ??
        (Number.isFinite(c.statusId)
          ? { id: c.statusId as number, nome: "" }
          : null);
      const stBase = normalizarStatusAnalise(statusComFallback);
      const criterioTemAlgumaSolicitacao = listarAnalisesCriterio(c.analise).some(
        (x) => Boolean(x?.solicitadoEm) && String(x.solicitadoEm).trim() !== "",
      );
      // Evita reabrir todos os critérios em PENDENTE quando só alguns entraram em adequação.
      const st =
        stBase === "" && !criterioTemAlgumaSolicitacao
          ? ultimoStatusNaoPendenteDoHistorico(c) || stBase
          : stBase;
      const retornoSindicatoDocumentado = listarAnalisesCriterio(c.analise).some(
        (x) =>
          Boolean(x?.solicitadoEm) &&
          String(x.solicitadoEm).trim() !== "" &&
          analiseCriterioComRespostaAposSolicitacao(x),
      );
      
      // Só é preciso "reiniciar" (zerar id e limpar campos) quando a própria linha
      // escolhida (a) ainda é o ciclo encerrado — ou seja, ainda não existe uma
      // linha nova para a rodada atual. Se `a` já é a linha nova (sem solicitadoEm),
      // seu id deve ser preservado para o PATCH atualizar a linha certa.
      const linhaSelecionadaEncerrada =
        Boolean(a?.solicitadoEm) &&
        String(a?.solicitadoEm).trim() !== "" &&
        analiseCriterioComRespostaAposSolicitacao(a);
      const reiniciarCamposNovaAnalise =
        retornoSindicatoDocumentado && st === "" && linhaSelecionadaEncerrada;
      const payload: AnaliseCamposPorSecao = {
        status: reiniciarCamposNovaAnalise ? "" : st,
        observacaoAnalista: reiniciarCamposNovaAnalise
          ? ""
          : String(a?.observacaoAnalista ?? ""),
        observacaoInterna: reiniciarCamposNovaAnalise
          ? ""
          : String(a?.observacaoInterna ?? "").slice(0, 200),
        // Ao iniciar uma nova rodada após retorno do sindicato, não reaproveitar o id
        // do ciclo encerrado; assim o PATCH persiste na linha atual (id=0) e evita
        // sobrescrever observações de ciclos antigos.
        registroAnaliseId: reiniciarCamposNovaAnalise
          ? null
          : a?.id != null && Number.isFinite(a.id)
            ? a.id
            : null,
        criterioIdApi:
          typeof c.criterioId === "number" &&
          Number.isFinite(c.criterioId) &&
          c.criterioId > 0
            ? c.criterioId
            : c.id,
      };
      for (const key of chavesCriterioAnalise(c)) {
        out[key] = payload;
      }
    }
  }
  return out;
}

